import { CHECKIN_RANK_LABELS, SPOT_MAKER_RANK_LABELS } from '@ridehub/shared';

interface UserRankBadgeProps {
  rank: string;
  type: 'checkin' | 'spotMaker';
  size?: 'sm' | 'lg' | 'xl';
  showLabel?: boolean;
}

// ── ヘルパー ────────────────────────────────────────────

function starPoints(px: number, py: number, s: number): string {
  const pts: [number, number][] = [
    [px, py - s],
    [px + s * 0.38, py - s * 0.12],
    [px + s * 0.95, py - s * 0.31],
    [px + s * 0.59, py + s * 0.35],
    [px + s * 0.59, py + s * 0.95],
    [px, py + s * 0.62],
    [px - s * 0.59, py + s * 0.95],
    [px - s * 0.59, py + s * 0.35],
    [px - s * 0.95, py - s * 0.31],
    [px - s * 0.38, py - s * 0.12],
  ];
  return pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
}

function arcPos(deg: number): [number, number] {
  const rad = (deg * Math.PI) / 180;
  return [22 + 15 * Math.cos(rad), 22 + 15 * Math.sin(rad)];
}

// ── チェックインバッジ ──────────────────────────────────

interface CheckinTokens {
  bgA: string;
  bgB: string;
  ringA: string;
  ringB: string;
  ringC: string;
  glowColor: string;
  glowOp: number;
  hasGlow: boolean;
  extraGlow?: boolean;
  bikeColor: string;
  markColor: string;
  ringCount: number;
  ringWidth: number;
  starAngles: number[];
  isLv5?: boolean;
}

const CHECKIN_TOKENS: Record<number, CheckinTokens> = {
  1: {
    bgA: '#f0fdf4',
    bgB: '#dcfce7',
    ringA: '#86efac',
    ringB: '#16a34a',
    ringC: '#86efac',
    glowColor: '#22c55e',
    glowOp: 0.18,
    hasGlow: false,
    bikeColor: '#15803d',
    markColor: '#16a34a',
    ringCount: 1,
    ringWidth: 1.5,
    starAngles: [90],
  },
  2: {
    bgA: '#eff6ff',
    bgB: '#dbeafe',
    ringA: '#93c5fd',
    ringB: '#2563eb',
    ringC: '#93c5fd',
    glowColor: '#3b82f6',
    glowOp: 0.2,
    hasGlow: true,
    bikeColor: '#1d4ed8',
    markColor: '#2563eb',
    ringCount: 2,
    ringWidth: 2,
    starAngles: [60, 120],
  },
  3: {
    bgA: '#f8fafc',
    bgB: '#f1f5f9',
    ringA: '#e2e8f0',
    ringB: '#64748b',
    ringC: '#cbd5e1',
    glowColor: '#94a3b8',
    glowOp: 0.15,
    hasGlow: true,
    bikeColor: '#475569',
    markColor: '#64748b',
    ringCount: 3,
    ringWidth: 2.2,
    starAngles: [55, 90, 125],
  },
  4: {
    bgA: '#fffdf0',
    bgB: '#fef9e0',
    ringA: '#fde68a',
    ringB: '#d97706',
    ringC: '#fef3c7',
    glowColor: '#f59e0b',
    glowOp: 0.22,
    hasGlow: true,
    bikeColor: '#b45309',
    markColor: '#d97706',
    ringCount: 4,
    ringWidth: 2.8,
    starAngles: [50, 75, 105, 130],
  },
  5: {
    bgA: '#1a1a22',
    bgB: '#0a0a10',
    ringA: '#fde68a',
    ringB: '#fbbf24',
    ringC: '#fde68a',
    glowColor: '#fbbf24',
    glowOp: 0.35,
    hasGlow: true,
    extraGlow: true,
    bikeColor: '#fbbf24',
    markColor: '#fde68a',
    ringCount: 5,
    ringWidth: 3.2,
    starAngles: [45, 67.5, 90, 112.5, 135],
    isLv5: true,
  },
};

function CheckinBadgeSVG({ level, sizePx }: { level: 1 | 2 | 3 | 4 | 5; sizePx: number }) {
  const t = CHECKIN_TOKENS[level];
  const id = `ci${level}`;
  const bgR = 21 - t.ringWidth - 0.2;
  const s = 2.6;
  const bc = t.bikeColor;
  const sw = 1.1;

  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 44" width={sizePx} height={sizePx}>
      <defs>
        <radialGradient id={`bg${id}`} cx="50%" cy="40%" r="65%">
          <stop offset="0%" stopColor={t.bgA} />
          <stop offset="100%" stopColor={t.bgB} />
        </radialGradient>
        <linearGradient id={`rg${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={t.ringA} />
          <stop offset="50%" stopColor={t.ringB} />
          <stop offset="100%" stopColor={t.ringC} />
        </linearGradient>
      </defs>

      {/* グロー */}
      {t.hasGlow && (
        <>
          <circle cx="22" cy="22" r="21.8" fill={t.glowColor} fillOpacity={t.glowOp * 0.6} />
          {t.extraGlow && (
            <circle cx="22" cy="22" r="21.8" fill={t.glowColor} fillOpacity={t.glowOp * 0.4} />
          )}
        </>
      )}

      {/* リング */}
      {t.ringCount >= 5 && (
        <circle
          cx="22"
          cy="22"
          r="21.9"
          fill="none"
          stroke={t.ringA}
          strokeWidth="0.8"
          opacity="0.7"
        />
      )}
      <circle
        cx="22"
        cy="22"
        r="21"
        fill="none"
        stroke={`url(#rg${id})`}
        strokeWidth={t.ringWidth}
      />
      {t.ringCount >= 2 && (
        <circle
          cx="22"
          cy="22"
          r="21.5"
          fill="none"
          stroke={t.ringA}
          strokeWidth="0.6"
          opacity="0.5"
        />
      )}
      {t.ringCount >= 3 && (
        <circle
          cx="22"
          cy="22"
          r={21 - t.ringWidth - 0.5}
          fill="none"
          stroke={t.ringB}
          strokeWidth="1"
          opacity="0.6"
        />
      )}
      {t.ringCount >= 4 && (
        <circle
          cx="22"
          cy="22"
          r={21 - t.ringWidth - 2}
          fill="none"
          stroke={t.ringA}
          strokeWidth="0.7"
          strokeDasharray="2 2"
          opacity="0.5"
        />
      )}
      {t.ringCount >= 5 && (
        <circle
          cx="22"
          cy="22"
          r={21 - t.ringWidth - 3.2}
          fill="none"
          stroke={t.ringB}
          strokeWidth="0.5"
          strokeDasharray="1.5 2"
          opacity="0.4"
        />
      )}

      {/* バッジ背景 */}
      <circle cx="22" cy="22" r={bgR} fill={`url(#bg${id})`} />

      {/* ロードバイクアイコン */}
      <circle cx="13" cy="24" r="4" fill="none" stroke={bc} strokeWidth={sw} />
      <circle cx="31" cy="24" r="4" fill="none" stroke={bc} strokeWidth={sw} />
      <line x1="13" y1="24" x2="22" y2="22.5" stroke={bc} strokeWidth={sw} />
      <line x1="13" y1="24" x2="20" y2="14.5" stroke={bc} strokeWidth={sw * 0.85} />
      <line x1="22" y1="22.5" x2="20" y2="14.5" stroke={bc} strokeWidth={sw} />
      <line x1="20" y1="14.5" x2="27" y2="15.5" stroke={bc} strokeWidth={sw} />
      <line x1="27" y1="15.5" x2="22" y2="22.5" stroke={bc} strokeWidth={sw} />
      <line x1="27" y1="15.5" x2="31" y2="24" stroke={bc} strokeWidth={sw * 0.9} />
      <line x1="20" y1="14.5" x2="19.5" y2="11.5" stroke={bc} strokeWidth={sw * 0.9} />
      <line
        x1="17.5"
        y1="11.5"
        x2="21.5"
        y2="11"
        stroke={bc}
        strokeWidth={sw * 1.1}
        strokeLinecap="round"
      />
      <line x1="27" y1="15.5" x2="27" y2="13" stroke={bc} strokeWidth={sw * 0.9} />
      <line
        x1="25.2"
        y1="13"
        x2="28.8"
        y2="13"
        stroke={bc}
        strokeWidth={sw}
        strokeLinecap="round"
      />
      <path
        d="M28.8,13 C29.8,13 30.2,13.8 30.2,15.2"
        fill="none"
        stroke={bc}
        strokeWidth={sw * 0.9}
        strokeLinecap="round"
      />

      {/* 星（弧状配置） */}
      {t.starAngles.map((deg, i) => {
        const [px, py] = arcPos(deg);
        const pts = starPoints(px, py, s);
        return (
          <g key={i}>
            {!t.isLv5 && (
              <polygon
                points={pts}
                fill="white"
                fillOpacity="0.7"
                stroke="white"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            )}
            <polygon points={pts} fill={t.markColor} fillOpacity="0.9" />
          </g>
        );
      })}
    </svg>
  );
}

function CheckinNone({ sizePx }: { sizePx: number }) {
  return (
    <svg width={sizePx} height={sizePx} viewBox="0 0 44 44" fill="none">
      <circle cx="22" cy="22" r="20" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1.5" />
      <text x="22" y="27" textAnchor="middle" fill="#94a3b8" fontSize="14" fontWeight="bold">
        −
      </text>
    </svg>
  );
}

function CheckinIcon({ rank, sizePx }: { rank: string; sizePx: number }) {
  const lvMap: Record<string, 1 | 2 | 3 | 4 | 5> = { lv1: 1, lv2: 2, lv3: 3, lv4: 4, lv5: 5 };
  const lv = lvMap[rank];
  if (!lv) return <CheckinNone sizePx={sizePx} />;
  return <CheckinBadgeSVG level={lv} sizePx={sizePx} />;
}

// ── スポットメイカーバッジ ──────────────────────────────

const SHIELD = 'M28,3 L5,12 V30 C5,44.5 15,56.5 28,60 C41,56.5 51,44.5 51,30 V12 Z';

interface SpotMakerTokens {
  frameFrom: string;
  frameMid: string;
  frameTo: string;
  bodyFrom: string;
  bodyTo: string;
  shadow: string;
  iconFill: string;
  iconBody: string;
  chevronColor: string;
  stroke: string;
  chevrons: number;
  isLv5?: boolean;
}

const SPOT_MAKER_TOKENS: Record<number, SpotMakerTokens> = {
  1: {
    frameFrom: '#86efac',
    frameMid: '#16a34a',
    frameTo: '#86efac',
    bodyFrom: '#f0fdf4',
    bodyTo: '#bbf7d0',
    shadow: '#16a34a',
    iconFill: '#15803d',
    iconBody: '#f0fdf4',
    chevronColor: '#15803d',
    stroke: '#15803d',
    chevrons: 1,
  },
  2: {
    frameFrom: '#93c5fd',
    frameMid: '#2563eb',
    frameTo: '#93c5fd',
    bodyFrom: '#eff6ff',
    bodyTo: '#bfdbfe',
    shadow: '#2563eb',
    iconFill: '#1d4ed8',
    iconBody: '#eff6ff',
    chevronColor: '#1d4ed8',
    stroke: '#1e40af',
    chevrons: 2,
  },
  3: {
    frameFrom: '#f1f5f9',
    frameMid: '#94a3b8',
    frameTo: '#e2e8f0',
    bodyFrom: '#f8fafc',
    bodyTo: '#e2e8f0',
    shadow: '#94a3b8',
    iconFill: '#475569',
    iconBody: '#f8fafc',
    chevronColor: '#64748b',
    stroke: '#64748b',
    chevrons: 3,
  },
  4: {
    frameFrom: '#fef9e0',
    frameMid: '#f59e0b',
    frameTo: '#d97706',
    bodyFrom: '#fffdf0',
    bodyTo: '#fef3c7',
    shadow: '#d97706',
    iconFill: '#b45309',
    iconBody: '#fffdf0',
    chevronColor: '#b45309',
    stroke: '#92400e',
    chevrons: 4,
  },
  5: {
    frameFrom: '#2a2a35',
    frameMid: '#111118',
    frameTo: '#1a1a22',
    bodyFrom: '#1e1e28',
    bodyTo: '#0e0e14',
    shadow: '#111118',
    iconFill: '#fbbf24',
    iconBody: '#1e1e28',
    chevronColor: '#fbbf24',
    stroke: '#fbbf24',
    chevrons: 5,
    isLv5: true,
  },
};

function SpotMakerBadgeSVG({
  level,
  widthPx,
  heightPx,
}: {
  level: 1 | 2 | 3 | 4 | 5;
  widthPx: number;
  heightPx: number;
}) {
  const t = SPOT_MAKER_TOKENS[level];
  const id = `sm${level}`;
  const CHEV_SPACING = 4.5;
  const CHEV_BOTTOM = 46.5;
  const halfW = 10;
  const startY = CHEV_BOTTOM - (t.chevrons - 1) * CHEV_SPACING;
  const knockoutColor = t.isLv5 ? '#0a0a10' : 'white';

  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 64" width={widthPx} height={heightPx}>
      <defs>
        {t.isLv5 ? (
          <linearGradient id={`fg${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3a3a48" />
            <stop offset="45%" stopColor="#111118" />
            <stop offset="100%" stopColor="#222230" />
          </linearGradient>
        ) : (
          <linearGradient id={`fg${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={t.frameFrom} />
            <stop offset="50%" stopColor={t.frameMid} />
            <stop offset="100%" stopColor={t.frameTo} />
          </linearGradient>
        )}
        <linearGradient id={`bg${id}`} x1="20%" y1="0%" x2="80%" y2="100%">
          <stop offset="0%" stopColor={t.bodyFrom} />
          <stop offset="100%" stopColor={t.bodyTo} />
        </linearGradient>
        <linearGradient id={`sh${id}`} x1="10%" y1="0%" x2="70%" y2="80%">
          <stop offset="0%" stopColor="#fff" stopOpacity={t.isLv5 ? '0.07' : '0.75'} />
          <stop offset="50%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <clipPath id={`cp${id}`}>
          <path d={SHIELD} />
        </clipPath>
      </defs>

      {/* ドロップシャドウ */}
      <path transform="translate(0.8,1.8)" d={SHIELD} fill={t.shadow} opacity="0.13" />

      {/* フレーム */}
      <path
        d={SHIELD}
        fill={`url(#fg${id})`}
        stroke={t.stroke}
        strokeWidth={t.isLv5 ? '1.2' : '0.8'}
      />

      {/* Lv5 ゴールド内枠 */}
      {t.isLv5 && (
        <path
          transform="translate(28,32) scale(0.935) translate(-28,-32)"
          d={SHIELD}
          fill="none"
          stroke="#fbbf24"
          strokeWidth="1"
          opacity="0.7"
        />
      )}

      {/* ボディ */}
      <path
        transform="translate(28,32) scale(0.9) translate(-28,-32)"
        d={SHIELD}
        fill={`url(#bg${id})`}
      />

      {/* シャイン */}
      <path
        transform="translate(28,32) scale(0.9) translate(-28,-32)"
        d={SHIELD}
        fill={`url(#sh${id})`}
      />

      {/* クリップ内コンテンツ */}
      <g clipPath={`url(#cp${id})`}>
        {/* 山岳アイコン */}
        <line
          x1="11"
          y1="36"
          x2="45"
          y2="36"
          stroke={t.iconFill}
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.35"
        />
        <polygon points="11,36 19,22 27,36" fill={t.iconFill} opacity="0.35" />
        <polygon points="19,36 31,11 43,36" fill={t.iconFill} opacity="0.7" />
        <polygon points="31,11 27,18 35,18" fill={t.iconBody} opacity="0.65" />
        <line x1="13" y1="40" x2="43" y2="40" stroke={t.iconFill} strokeWidth="0.6" opacity="0.2" />

        {/* シェブロン */}
        {Array.from({ length: t.chevrons }, (_, i) => {
          const cy = startY + i * CHEV_SPACING;
          const pts = `${28 - halfW},${cy} 28,${cy - 4} ${28 + halfW},${cy}`;
          return (
            <g key={i}>
              <polyline
                points={pts}
                fill="none"
                stroke={knockoutColor}
                strokeWidth="4.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.7"
              />
              <polyline
                points={pts}
                fill="none"
                stroke={t.chevronColor}
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          );
        })}
      </g>
    </svg>
  );
}

function SpotMakerNone({ widthPx, heightPx }: { widthPx: number; heightPx: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={widthPx} height={heightPx} viewBox="0 0 56 64">
      <path d={SHIELD} fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1" />
    </svg>
  );
}

function SpotMakerIcon({
  rank,
  widthPx,
  heightPx,
}: {
  rank: string;
  widthPx: number;
  heightPx: number;
}) {
  const lvMap: Record<string, 1 | 2 | 3 | 4 | 5> = { lv1: 1, lv2: 2, lv3: 3, lv4: 4, lv5: 5 };
  const lv = lvMap[rank];
  if (!lv) return <SpotMakerNone widthPx={widthPx} heightPx={heightPx} />;
  return <SpotMakerBadgeSVG level={lv} widthPx={widthPx} heightPx={heightPx} />;
}

// ── メインコンポーネント ────────────────────────────────

export function UserRankBadge({ rank, type, size = 'sm', showLabel = true }: UserRankBadgeProps) {
  if (!rank) return null;

  const labels = type === 'checkin' ? CHECKIN_RANK_LABELS : SPOT_MAKER_RANK_LABELS;
  const label = labels[rank] ?? rank;

  const sizePx = size === 'xl' ? 48 : size === 'lg' ? 28 : 20;
  const icon =
    type === 'checkin' ? (
      <CheckinIcon rank={rank} sizePx={sizePx} />
    ) : (
      <SpotMakerIcon
        rank={rank}
        widthPx={size === 'xl' ? 47 : size === 'lg' ? 24 : 18}
        heightPx={size === 'xl' ? 54 : sizePx}
      />
    );

  if (size === 'sm' || !showLabel) {
    return icon;
  }

  return (
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-sm text-gray-700 font-medium">{label}</span>
    </div>
  );
}
